import React, { useImperativeHandle, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import './CustomBarChart.scss';
import { EChartsOption } from 'echarts';
import { Skeleton } from 'primereact/skeleton';
import { useTheme } from '../../../contexts/theme/ThemeContext';
import LegendItem from './components/legend-item/LegendItem';

export interface CustomBarEChartsOption {
  eChartsOption: EChartsOption | null;
  legendData: LegendData[] | null;
}

export interface LegendData {
  color: string;
  label: string;
}

interface CustomBarChartProps {
  chartSize?: 'SM' | 'MD' | 'LG';
  type?: 'VERTICAL' | 'HORIZONTAL';
  customEChartsOption: CustomBarEChartsOption;
  legendStateMap: Map<string, boolean>;
  onLegendClick: (label: string) => void;
  chartStyle: React.CSSProperties;
  notMerge?: boolean;
  /** Wystawia instancję ECharts na zewnątrz — raport zrzuca z niej wykres do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
}

const CustomBarChart: React.FC<CustomBarChartProps> = ({
  chartSize = 'SM',
  type = 'VERTICAL',
  customEChartsOption,
  legendStateMap,
  onLegendClick,
  chartStyle,
  notMerge = false,
  chartRef,
}) => {
  const eChartsRef = useRef<ReactECharts>(null);
  useImperativeHandle(chartRef, () => eChartsRef.current!, []);
  const { theme } = useTheme();

  if (!customEChartsOption || !customEChartsOption.eChartsOption) {
    return (
      <div className="custom-bar-chart">
        <div className="custom-bar-chart__legend">
          <Skeleton width="200px" height="20px" />
        </div>
        <div className="custom-bar-chart__box">
          <Skeleton width="100%" height="300px" />
        </div>
      </div>
    );
  }

  const renderLegendItem = (index: number, data: LegendData) => {
    return (
      <LegendItem
        key={`${data.label}-${index}`}
        isSelected={legendStateMap.get(data.label) ?? true}
        label={data.label}
        color={data.color}
        legendItemHandler={onLegendClick}
      />
    );
  };

  return (
    <div className={`custom-bar-chart custom-bar-chart--${chartSize.toLowerCase()}`}>
      {customEChartsOption.legendData && (
        <div className={`custom-bar-chart__legend custom-bar-chart__legend--${type.toLowerCase()}`}>
          {customEChartsOption.legendData.map((data, index) => renderLegendItem(index, data))}
        </div>
      )}
      <div className="custom-bar-chart__box">
        <ReactECharts
          key={theme}
          ref={eChartsRef}
          option={customEChartsOption.eChartsOption}
          style={chartStyle}
          notMerge={notMerge}
        />
      </div>
    </div>
  );
};

export default CustomBarChart;
